import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Sparkles, Flame, Award, Lock, ChevronDown, ChevronUp, Share2 } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

/* ============================================================
   Australian Animal SVGs — cartoon/illustrated style
   ============================================================ */

const KoalaSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <circle cx="32" cy="36" r="18" fill="#8B8682" />
    <circle cx="20" cy="22" r="10" fill="#8B8682" />
    <circle cx="44" cy="22" r="10" fill="#8B8682" />
    <circle cx="20" cy="22" r="6" fill="#D4A9A0" />
    <circle cx="44" cy="22" r="6" fill="#D4A9A0" />
    <circle cx="32" cy="36" r="14" fill="#A8A29E" />
    <ellipse cx="32" cy="38" rx="6" ry="4" fill="#F5F0EB" />
    <ellipse cx="32" cy="36" rx="4" ry="3" fill="#3D3129" />
    <circle cx="27" cy="31" r="2.5" fill="#3D3129" />
    <circle cx="37" cy="31" r="2.5" fill="#3D3129" />
    <circle cx="28" cy="30" r="1" fill="white" />
    <circle cx="38" cy="30" r="1" fill="white" />
  </svg>
);

const KangarooSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <path d="M38 58 Q42 48 40 38" stroke="#A0785A" strokeWidth="4" fill="none" strokeLinecap="round" />
    <ellipse cx="30" cy="40" rx="12" ry="16" fill="#C4956A" />
    <ellipse cx="30" cy="46" rx="8" ry="8" fill="#DEB896" />
    <circle cx="30" cy="22" r="10" fill="#C4956A" />
    <path d="M24 14 L22 6" stroke="#C4956A" strokeWidth="3" strokeLinecap="round" />
    <path d="M36 14 L38 6" stroke="#C4956A" strokeWidth="3" strokeLinecap="round" />
    <circle cx="27" cy="20" r="2" fill="#3D3129" />
    <circle cx="33" cy="20" r="2" fill="#3D3129" />
    <ellipse cx="30" cy="24" rx="2" ry="1.5" fill="#3D3129" />
    <ellipse cx="22" cy="56" rx="4" ry="3" fill="#C4956A" />
    <ellipse cx="38" cy="56" rx="4" ry="3" fill="#C4956A" />
  </svg>
);

const WombatSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="42" rx="18" ry="14" fill="#7C6F64" />
    <circle cx="32" cy="28" r="12" fill="#7C6F64" />
    <circle cx="24" cy="20" r="5" fill="#7C6F64" />
    <circle cx="40" cy="20" r="5" fill="#7C6F64" />
    <circle cx="24" cy="20" r="3" fill="#A89C94" />
    <circle cx="40" cy="20" r="3" fill="#A89C94" />
    <circle cx="28" cy="26" r="2.5" fill="#3D3129" />
    <circle cx="36" cy="26" r="2.5" fill="#3D3129" />
    <circle cx="29" cy="25" r="1" fill="white" />
    <circle cx="37" cy="25" r="1" fill="white" />
    <ellipse cx="32" cy="31" rx="3" ry="2" fill="#3D3129" />
    <ellipse cx="22" cy="54" rx="5" ry="3" fill="#7C6F64" />
    <ellipse cx="42" cy="54" rx="5" ry="3" fill="#7C6F64" />
  </svg>
);

const PlatypusSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="38" rx="16" ry="12" fill="#6B5A4E" />
    <path d="M16 38 Q8 36 6 40 Q8 44 16 42" fill="#D4956A" />
    <ellipse cx="48" cy="42" rx="6" ry="3" fill="#6B5A4E" />
    <circle cx="30" cy="30" r="8" fill="#6B5A4E" />
    <ellipse cx="30" cy="34" rx="7" ry="3" fill="#8B7355" />
    <circle cx="27" cy="28" r="2" fill="#3D3129" />
    <circle cx="33" cy="28" r="2" fill="#3D3129" />
    <ellipse cx="24" cy="50" rx="4" ry="2" fill="#D4956A" />
    <ellipse cx="40" cy="50" rx="4" ry="2" fill="#D4956A" />
  </svg>
);

const EchidnaSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="40" rx="16" ry="14" fill="#8B7355" />
    {[0,1,2,3,4,5,6,7].map(i => (
      <line key={i} x1={22 + i*3} y1={28 + (i%2)*2} x2={20 + i*3} y2={20 + (i%2)*2} stroke="#D4C4A8" strokeWidth="2" strokeLinecap="round" />
    ))}
    <circle cx="28" cy="38" r="6" fill="#A8947C" />
    <path d="M22 38 Q16 36 12 38" stroke="#8B7355" strokeWidth="3" fill="none" strokeLinecap="round" />
    <circle cx="26" cy="36" r="1.5" fill="#3D3129" />
    <circle cx="30" cy="36" r="1.5" fill="#3D3129" />
    <circle cx="12" cy="38" r="1.5" fill="#3D3129" />
    <ellipse cx="26" cy="52" rx="4" ry="2" fill="#8B7355" />
    <ellipse cx="38" cy="52" rx="4" ry="2" fill="#8B7355" />
  </svg>
);

const KookaburraSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="42" rx="10" ry="14" fill="#F5F0EB" />
    <circle cx="32" cy="24" r="10" fill="#8B7355" />
    <circle cx="32" cy="24" r="7" fill="#F5F0EB" />
    <circle cx="29" cy="22" r="2" fill="#3D3129" />
    <circle cx="35" cy="22" r="2" fill="#3D3129" />
    <path d="M32 26 L40 24 L32 28" fill="#D4956A" />
    <path d="M22 42 L14 46" stroke="#5B8CA8" strokeWidth="3" strokeLinecap="round" />
    <path d="M42 42 L50 46" stroke="#5B8CA8" strokeWidth="3" strokeLinecap="round" />
    <path d="M28 56 L28 60" stroke="#D4956A" strokeWidth="2" strokeLinecap="round" />
    <path d="M36 56 L36 60" stroke="#D4956A" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CockatooSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="42" rx="10" ry="14" fill="#F5F0EB" />
    <circle cx="32" cy="24" r="10" fill="#F5F0EB" />
    <path d="M28 14 L26 4" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" />
    <path d="M32 14 L32 2" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" />
    <path d="M36 14 L38 4" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" />
    <circle cx="28" cy="22" r="2.5" fill="#3D3129" />
    <circle cx="36" cy="22" r="2.5" fill="#3D3129" />
    <circle cx="29" cy="21" r="1" fill="white" />
    <circle cx="37" cy="21" r="1" fill="white" />
    <path d="M32 27 L36 26 L32 29" fill="#3D3129" />
    <circle cx="28" cy="28" r="3" fill="#FFB6C1" opacity="0.5" />
    <circle cx="36" cy="28" r="3" fill="#FFB6C1" opacity="0.5" />
    <path d="M28 56 L28 60" stroke="#555" strokeWidth="2" strokeLinecap="round" />
    <path d="M36 56 L36 60" stroke="#555" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PossumSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="42" rx="14" ry="14" fill="#6B5B4E" />
    <circle cx="32" cy="26" r="10" fill="#6B5B4E" />
    <path d="M24 18 L20 8" stroke="#6B5B4E" strokeWidth="3" strokeLinecap="round" />
    <path d="M40 18 L44 8" stroke="#6B5B4E" strokeWidth="3" strokeLinecap="round" />
    <circle cx="20" cy="8" r="3" fill="#D4A9A0" />
    <circle cx="44" cy="8" r="3" fill="#D4A9A0" />
    <circle cx="28" cy="24" r="3" fill="#3D3129" />
    <circle cx="36" cy="24" r="3" fill="#3D3129" />
    <circle cx="29" cy="23" r="1.2" fill="white" />
    <circle cx="37" cy="23" r="1.2" fill="white" />
    <ellipse cx="32" cy="29" rx="2" ry="1.5" fill="#FFB6C1" />
    <path d="M46 38 Q54 30 56 38 Q54 42 46 42" fill="#6B5B4E" />
  </svg>
);

const QuokkaSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="42" rx="14" ry="14" fill="#A0785A" />
    <circle cx="32" cy="26" r="12" fill="#A0785A" />
    <circle cx="22" cy="18" r="5" fill="#A0785A" />
    <circle cx="42" cy="18" r="5" fill="#A0785A" />
    <circle cx="22" cy="18" r="3" fill="#C4956A" />
    <circle cx="42" cy="18" r="3" fill="#C4956A" />
    <circle cx="32" cy="26" r="8" fill="#C4956A" />
    <circle cx="28" cy="24" r="2.5" fill="#3D3129" />
    <circle cx="36" cy="24" r="2.5" fill="#3D3129" />
    <circle cx="29" cy="23" r="1" fill="white" />
    <circle cx="37" cy="23" r="1" fill="white" />
    <ellipse cx="32" cy="29" rx="2.5" ry="1.5" fill="#3D3129" />
    <path d="M27 31 Q32 35 37 31" stroke="#3D3129" strokeWidth="1.5" fill="none" strokeLinecap="round" />
  </svg>
);

const FairyWrenSVG = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    <ellipse cx="32" cy="40" rx="10" ry="12" fill="#6B5B4E" />
    <circle cx="30" cy="26" r="9" fill="#4A7CBA" />
    <circle cx="30" cy="26" r="6" fill="#5B98D4" />
    <circle cx="28" cy="24" r="2" fill="#3D3129" />
    <circle cx="33" cy="24" r="2" fill="#3D3129" />
    <path d="M30 28 L35 26" stroke="#D4956A" strokeWidth="2" strokeLinecap="round" />
    <path d="M22 38 L14 42" stroke="#6B5B4E" strokeWidth="3" strokeLinecap="round" />
    <path d="M42 38 L50 42" stroke="#6B5B4E" strokeWidth="3" strokeLinecap="round" />
    <path d="M36 50 Q44 56 48 60" stroke="#4A7CBA" strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M28 52 L28 58" stroke="#D4956A" strokeWidth="2" strokeLinecap="round" />
    <path d="M36 52 L36 58" stroke="#D4956A" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ANIMAL_COMPONENTS = {
  koala: KoalaSVG,
  kangaroo: KangarooSVG,
  wombat: WombatSVG,
  platypus: PlatypusSVG,
  echidna: EchidnaSVG,
  kookaburra: KookaburraSVG,
  cockatoo: CockatooSVG,
  possum: PossumSVG,
  quokka: QuokkaSVG,
  fairy_wren: FairyWrenSVG,
};

/* ============================================================
   Plant SVGs (original)
   ============================================================ */

const SeedPlant = ({ color }) => (
  <svg viewBox="0 0 40 60" className="w-8 h-12">
    <ellipse cx="20" cy="50" rx="8" ry="4" fill="#8B4513" opacity="0.6" />
    <circle cx="20" cy="45" r="6" fill={color} opacity="0.8" />
  </svg>
);

const SproutPlant = ({ color }) => (
  <svg viewBox="0 0 40 60" className="w-8 h-12">
    <ellipse cx="20" cy="55" rx="10" ry="4" fill="#8B4513" opacity="0.5" />
    <path d="M20 55 L20 35" stroke="#228B22" strokeWidth="3" />
    <ellipse cx="15" cy="35" rx="6" ry="4" fill={color} transform="rotate(-30 15 35)" />
    <ellipse cx="25" cy="38" rx="5" ry="3" fill={color} transform="rotate(30 25 38)" />
  </svg>
);

const BloomPlant = ({ color }) => (
  <svg viewBox="0 0 40 60" className="w-8 h-12">
    <ellipse cx="20" cy="55" rx="10" ry="4" fill="#8B4513" opacity="0.5" />
    <path d="M20 55 L20 25" stroke="#228B22" strokeWidth="3" />
    <ellipse cx="12" cy="35" rx="6" ry="4" fill="#22C55E" transform="rotate(-45 12 35)" />
    <ellipse cx="28" cy="35" rx="6" ry="4" fill="#22C55E" transform="rotate(45 28 35)" />
    <circle cx="20" cy="20" r="8" fill={color} />
    <circle cx="20" cy="20" r="4" fill="#FCD34D" />
  </svg>
);

const FlowerPlant = ({ color }) => (
  <svg viewBox="0 0 40 70" className="w-10 h-14">
    <ellipse cx="20" cy="65" rx="12" ry="4" fill="#8B4513" opacity="0.5" />
    <path d="M20 65 L20 25" stroke="#228B22" strokeWidth="4" />
    <ellipse cx="10" cy="45" rx="8" ry="5" fill="#22C55E" transform="rotate(-30 10 45)" />
    <ellipse cx="30" cy="45" rx="8" ry="5" fill="#22C55E" transform="rotate(30 30 45)" />
    <ellipse cx="20" cy="12" rx="5" ry="8" fill={color} />
    <ellipse cx="12" cy="18" rx="5" ry="8" fill={color} transform="rotate(-72 12 18)" />
    <ellipse cx="28" cy="18" rx="5" ry="8" fill={color} transform="rotate(72 28 18)" />
    <circle cx="20" cy="20" r="5" fill="#FCD34D" />
  </svg>
);

const TreePlant = ({ color }) => (
  <svg viewBox="0 0 50 80" className="w-12 h-16">
    <rect x="22" y="50" width="6" height="25" fill="#8B4513" />
    <ellipse cx="25" cy="75" rx="15" ry="4" fill="#8B4513" opacity="0.4" />
    <ellipse cx="25" cy="30" rx="20" ry="25" fill="#22C55E" />
    <ellipse cx="15" cy="25" rx="12" ry="15" fill={color} opacity="0.7" />
    <ellipse cx="35" cy="25" rx="12" ry="15" fill={color} opacity="0.7" />
    <ellipse cx="25" cy="15" rx="10" ry="12" fill={color} opacity="0.8" />
    <circle cx="18" cy="35" r="3" fill={color} />
    <circle cx="32" cy="30" r="3" fill={color} />
    <circle cx="25" cy="20" r="3" fill={color} />
  </svg>
);

const PlantComponent = ({ stage, color }) => {
  switch (stage) {
    case "seed": return <SeedPlant color={color} />;
    case "sprout": return <SproutPlant color={color} />;
    case "bloom": return <BloomPlant color={color} />;
    case "flower": return <FlowerPlant color={color} />;
    case "tree": return <TreePlant color={color} />;
    default: return <SeedPlant color={color} />;
  }
};

const stageLabels = { seed: "Seed", sprout: "Sprout", bloom: "Bloom", flower: "Flower", tree: "Tree" };

/* Canvas text wrapping helper */
function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line + (line ? " " : "") + word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/* ============================================================
   Milestone icon helper
   ============================================================ */
const milestoneIconMap = {
  seed: "🌱", sprout: "🌿", bloom: "🌸", flower: "🌺", tree: "🌳",
  streak: "🔥", meditation: "🧘", breathwork: "💨", mood: "🌻", points: "⭐",
};

/* ============================================================
   Main component
   ============================================================ */
export const SelfGarden = () => {
  const { token } = useAuth();
  const [garden, setGarden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnimals, setShowAnimals] = useState(false);
  const [showMilestones, setShowMilestones] = useState(false);
  const [sponsorBadge, setSponsorBadge] = useState(null);

  useEffect(() => { fetchGarden(); }, []);

  const fetchGarden = async () => {
    try {
      const [gardenRes, badgeRes] = await Promise.all([
        axios.get(`${API}/garden`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sponsorship/tiers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
      ]);
      setGarden(gardenRes.data);
      if (badgeRes?.data?.active_sponsorship) {
        setSponsorBadge(badgeRes.data.active_sponsorship);
      }
    } catch (error) {
      console.error("Error fetching garden:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card rounded-2xl border-0">
        <CardContent className="p-6">
          <div className="animate-pulse flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-3 bg-muted rounded w-3/4" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!garden) return null;

  const {
    current_stage, stage_progress, plants, total_points,
    activity_counts, current_streak, longest_streak,
    milestones, rhythm_milestones, unlocked_animals, next_animal, total_animals,
  } = garden;

  const mainColor = plants?.length > 0 ? plants[plants.length - 1]?.color : "#10B981";
  const earnedMilestones = milestones?.filter(m => m.unlocked) || [];
  const lockedMilestones = milestones?.filter(m => !m.unlocked) || [];

  const generateShareImage = async () => {
    const canvas = document.createElement("canvas");
    const w = 600, h = 800;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#ecfdf5");
    grad.addColorStop(1, "#d1fae5");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Border
    ctx.strokeStyle = "#10B981";
    ctx.lineWidth = 4;
    ctx.roundRect(10, 10, w - 20, h - 20, 20);
    ctx.stroke();

    // Title
    ctx.fillStyle = "#064e3b";
    ctx.font = "bold 28px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("My Self Garden", w / 2, 60);

    // Subtitle
    ctx.fillStyle = "#6b7280";
    ctx.font = "16px sans-serif";
    ctx.fillText("Authentic Self 365", w / 2, 88);

    // Stage circle
    ctx.beginPath();
    ctx.arc(w / 2, 170, 50, 0, Math.PI * 2);
    ctx.fillStyle = "#d1fae520";
    ctx.fill();
    ctx.strokeStyle = "#10B981";
    ctx.lineWidth = 3;
    ctx.stroke();

    // Stage name
    ctx.fillStyle = "#065f46";
    ctx.font = "bold 22px sans-serif";
    ctx.fillText(stageLabels[current_stage] || "Seed", w / 2, 176);

    // Points
    ctx.fillStyle = "#6b7280";
    ctx.font = "14px sans-serif";
    ctx.fillText(`${total_points} points`, w / 2, 240);

    // Streaks
    let y = 280;
    ctx.fillStyle = "#064e3b";
    ctx.font = "bold 18px sans-serif";
    ctx.fillText(`Current Streak: ${current_streak} days`, w / 2, y);
    y += 28;
    ctx.fillStyle = "#6b7280";
    ctx.font = "14px sans-serif";
    ctx.fillText(`Longest Streak: ${longest_streak} days`, w / 2, y);

    // Milestones
    y += 50;
    if (earnedMilestones.length > 0) {
      ctx.fillStyle = "#064e3b";
      ctx.font = "bold 18px sans-serif";
      ctx.fillText(`Milestones Earned: ${earnedMilestones.length}`, w / 2, y);
      y += 30;
      ctx.font = "14px sans-serif";
      ctx.fillStyle = "#92400e";
      const milestoneNames = earnedMilestones.map(m => m.name).join("  •  ");
      const lines = wrapText(ctx, milestoneNames, w - 80);
      lines.forEach(line => {
        ctx.fillText(line, w / 2, y);
        y += 22;
      });
    }

    // Animals
    y += 30;
    if (unlocked_animals?.length > 0) {
      ctx.fillStyle = "#064e3b";
      ctx.font = "bold 18px sans-serif";
      ctx.fillText(`Garden Companions: ${unlocked_animals.length}/${total_animals}`, w / 2, y);
      y += 30;
      ctx.font = "14px sans-serif";
      ctx.fillStyle = "#065f46";
      const animalNames = unlocked_animals.map(a => a.name).join("  •  ");
      const lines = wrapText(ctx, animalNames, w - 80);
      lines.forEach(line => {
        ctx.fillText(line, w / 2, y);
        y += 22;
      });
    } else {
      ctx.fillStyle = "#6b7280";
      ctx.font = "14px sans-serif";
      ctx.fillText("Garden Companions: Keep your streak going!", w / 2, y);
    }

    // Footer
    ctx.fillStyle = "#9ca3af";
    ctx.font = "italic 13px sans-serif";
    ctx.fillText("authenticself365.com", w / 2, h - 30);

    return canvas;
  };

  const handleShareGarden = async () => {
    try {
      const canvas = await generateShareImage();
      canvas.toBlob(async (blob) => {
        if (navigator.share && navigator.canShare) {
          const file = new File([blob], "my-self-garden.png", { type: "image/png" });
          const shareData = { title: "My Self Garden", text: "Check out my Self Garden on Authentic Self 365!", files: [file] };
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        }
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "my-self-garden.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Garden image downloaded!");
      }, "image/png");
    } catch (err) {
      console.error("Share error:", err);
      toast.error("Could not share garden");
    }
  };

  return (
    <Card
      className="rounded-3xl border border-stone-200/70 shadow-[0_2px_20px_rgb(28,36,33,0.05)] overflow-hidden h-full"
      style={{
        backgroundImage: "linear-gradient(rgba(253,251,247,0.9), rgba(253,251,247,0.94)), url('https://images.unsplash.com/photo-1609998277445-c238f9d1d024?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
      data-testid="self-garden"
    >
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading text-lg font-semibold text-foreground">My Self Garden</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShareGarden}
              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
              title="Share your garden"
              data-testid="share-garden-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            {current_streak > 0 && (
              <div className="flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 px-2.5 py-1 rounded-full" data-testid="garden-streak-badge">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">{current_streak}d</span>
              </div>
            )}
          </div>
        </div>

        {/* Dandelion Doer Badge */}
        {sponsorBadge && (
          <div className="mb-4 flex items-center gap-3 px-3 py-2 rounded-xl border border-amber-200/60 dark:border-amber-800/40" style={{ background: "linear-gradient(135deg, #e0f7fa20, #fff8e120)" }} data-testid="dandelion-doer-badge">
            <svg viewBox="0 0 32 56" className="w-6 h-10 flex-shrink-0">
              <line x1="16" y1="56" x2="16" y2="24" stroke="#4DB6AC" strokeWidth="1.5" />
              <circle cx="16" cy="18" r="8" fill="#FFD54F" opacity="0.3" />
              <circle cx="16" cy="18" r="4" fill="#FFD54F" opacity="0.7" />
              <circle cx="16" cy="18" r="2" fill="#FFA000" />
              {[0, 60, 120, 180, 240, 300].map((a) => {
                const r = (a * Math.PI) / 180;
                return <line key={`ray-${a}`} x1="16" y1="18" x2={16 + Math.cos(r) * 10} y2={18 + Math.sin(r) * 10} stroke="#FFD54F" strokeWidth="0.7" opacity="0.5" />;
              })}
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold" style={{ color: "#00695C" }}>Dandelion Doer</p>
              <p className="text-[10px] text-amber-600">{sponsorBadge.tier_id?.charAt(0).toUpperCase() + sponsorBadge.tier_id?.slice(1)} Sponsor</p>
            </div>
          </div>
        )}

        {/* Main Plant + Stage */}
        <div className="flex items-center gap-6 mb-5">
          <div className="relative flex-shrink-0">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 flex items-center justify-center">
              <PlantComponent stage={current_stage} color={mainColor} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-foreground">{stageLabels[current_stage]}</span>
              <span className="text-sm text-muted-foreground">{total_points} pts</span>
            </div>
            {stage_progress?.next_stage && (
              <>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <span>Next: {stageLabels[stage_progress.next_stage]}</span>
                  <span>({stage_progress.current}/{stage_progress.needed})</span>
                </div>
                <Progress value={stage_progress.progress} className="h-2 bg-green-100 dark:bg-green-900/30" />
              </>
            )}
            {current_stage === "tree" && (
              <p className="text-sm text-emerald-600 font-medium mt-1">Fully grown! Keep nurturing your garden.</p>
            )}
          </div>
        </div>

        {/* Golden Milestone Blooms */}
        {rhythm_milestones?.length > 0 && (
          <div className="mb-5 rounded-xl border border-amber-200/70 dark:border-amber-800/40 p-3" style={{ background: "linear-gradient(135deg, #FFFBEB80, #FDF6EC40)" }} data-testid="golden-blooms">
            <p className="text-xs font-semibold mb-2" style={{ color: "#B45309" }}>Golden Blooms — rhythm milestones</p>
            <div className="flex gap-4">
              {rhythm_milestones.map((days) => (
                <div key={days} className="flex flex-col items-center" title={`${days}-day Daily Rhythm streak milestone`} data-testid={`golden-bloom-${days}`}>
                  <svg viewBox="0 0 32 56" className="w-7 h-12" style={{ filter: "drop-shadow(0 0 6px #FBBF24AA)" }}>
                    <line x1="16" y1="56" x2="16" y2="24" stroke="#B45309" strokeWidth="1.5" />
                    <circle cx="16" cy="18" r="9" fill="#FCD34D" opacity="0.35" />
                    <circle cx="16" cy="18" r="5" fill="#FBBF24" opacity="0.8" />
                    <circle cx="16" cy="18" r="2.5" fill="#D97706" />
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
                      const r = (a * Math.PI) / 180;
                      return <line key={`gb-${a}`} x1="16" y1="18" x2={16 + Math.cos(r) * 11} y2={18 + Math.sin(r) * 11} stroke="#FBBF24" strokeWidth="0.8" opacity="0.6" />;
                    })}
                  </svg>
                  <span className="text-[10px] font-bold mt-0.5" style={{ color: "#B45309" }}>{days} days</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Australian Animals Section ── */}
        <div className="border-t border-green-200/50 dark:border-green-800/30 pt-4 mb-4">
          <button
            onClick={() => setShowAnimals(!showAnimals)}
            className="flex items-center justify-between w-full text-left"
            data-testid="toggle-animals-section"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                Garden Companions
              </span>
              <span className="text-xs text-muted-foreground">
                ({unlocked_animals?.length || 0}/{total_animals || 10} unlocked)
              </span>
            </div>
            {showAnimals ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {showAnimals && (
            <div className="mt-3 space-y-3" data-testid="animals-grid">
              {/* Unlocked */}
              {unlocked_animals?.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {unlocked_animals.map((animal) => {
                    const AnimalComp = ANIMAL_COMPONENTS[animal.id];
                    return (
                      <div
                        key={animal.id}
                        className="flex flex-col items-center p-2 rounded-xl bg-white/60 dark:bg-white/5 border border-emerald-200/60 dark:border-emerald-800/40 hover:scale-105 transition-transform"
                        title={animal.description}
                        data-testid={`animal-${animal.id}`}
                      >
                        <div className="w-10 h-10">
                          {AnimalComp && <AnimalComp />}
                        </div>
                        <span className="text-[10px] font-medium text-foreground mt-1 text-center leading-tight">{animal.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Next animal progress */}
              {next_animal && (
                <div className="bg-white/40 dark:bg-white/5 rounded-xl p-3 border border-dashed border-emerald-300 dark:border-emerald-700" data-testid="next-animal-progress">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center opacity-40">
                      {ANIMAL_COMPONENTS[next_animal.id] && (() => { const C = ANIMAL_COMPONENTS[next_animal.id]; return <C />; })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground">Next: {next_animal.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-1">
                        <Flame className="w-3 h-3 text-orange-400" />
                        <span>{next_animal.days_remaining} more streak days needed</span>
                      </div>
                      <Progress value={next_animal.progress} className="h-1.5 bg-green-100 dark:bg-green-900/30" />
                    </div>
                  </div>
                </div>
              )}

              {/* Locked animals preview */}
              {(() => {
                const ALL_ANIMALS = [
                  "koala","kangaroo","wombat","platypus","echidna",
                  "kookaburra","cockatoo","possum","quokka","fairy_wren"
                ];
                const unlockedIds = new Set((unlocked_animals || []).map(a => a.id));
                const locked = ALL_ANIMALS.filter(id => !unlockedIds.has(id) && id !== next_animal?.id);
                if (locked.length === 0) return null;
                return (
                  <div className="grid grid-cols-5 gap-2 opacity-30">
                    {locked.map(id => {
                      const C = ANIMAL_COMPONENTS[id];
                      const name = id.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase());
                      return (
                        <div key={id} className="flex flex-col items-center p-2 rounded-xl bg-muted/30">
                          <div className="w-10 h-10 relative">
                            {C && <C />}
                            <Lock className="w-4 h-4 text-muted-foreground absolute bottom-0 right-0" />
                          </div>
                          <span className="text-[10px] text-muted-foreground mt-1 text-center leading-tight">{name}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <p className="text-[10px] text-muted-foreground text-center italic">
                Unlock a new Australian companion for every 30-day activity streak
              </p>
            </div>
          )}
        </div>

        {/* ── Milestones / Badges Section ── */}
        <div className="border-t border-green-200/50 dark:border-green-800/30 pt-4 mb-4">
          <button
            onClick={() => setShowMilestones(!showMilestones)}
            className="flex items-center justify-between w-full text-left"
            data-testid="toggle-milestones-section"
          >
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-foreground">Milestones</span>
              <span className="text-xs text-muted-foreground">
                ({earnedMilestones.length}/{milestones?.length || 0})
              </span>
            </div>
            {showMilestones ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {showMilestones && (
            <div className="mt-3 space-y-2" data-testid="milestones-list">
              {/* Earned */}
              {earnedMilestones.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {earnedMilestones.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2.5 py-1.5 rounded-full"
                      title={m.description}
                      data-testid={`milestone-${m.id}`}
                    >
                      <span className="text-sm">{milestoneIconMap[m.icon] || "🏅"}</span>
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">{m.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {earnedMilestones.length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  Complete activities to earn your first milestone!
                </p>
              )}

              {/* Locked — show a compact preview */}
              {lockedMilestones.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {lockedMilestones.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center gap-1.5 bg-muted/30 border border-muted px-2.5 py-1.5 rounded-full opacity-40"
                      title={m.description}
                    >
                      <Lock className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{m.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Plants */}
        <div className="border-t border-green-200/50 dark:border-green-800/30 pt-4">
          <p className="text-xs text-muted-foreground mb-3">Recent growth:</p>
          <div className="flex flex-wrap gap-2">
            {plants?.slice(-8).map((plant, i) => {
              const isGolden = plant.type?.startsWith("rhythm_milestone") || plant.type === "rhythm_perfect_day";
              return (
                <div
                  key={plant.id || `${plant.type}-${i}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  style={{ backgroundColor: `${plant.color}20`, boxShadow: isGolden ? "0 0 8px #FBBF24AA" : "none" }}
                  title={`${plant.type}: +${plant.points} pts`}
                >
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: plant.color }} />
                </div>
              );
            })}
            {(!plants || plants.length === 0) && (
              <p className="text-xs text-muted-foreground italic">Complete activities to grow your garden!</p>
            )}
          </div>
        </div>

        {/* Activity Legend */}
        <div className="mt-4 pt-4 border-t border-green-200/50 dark:border-green-800/30">
          <p className="text-xs text-muted-foreground mb-2">Grow by:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#8B5CF6]" />Meditate</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#06B6D4]" />Breathe</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]" />Journal</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]" />Learn</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EC4899]" />Check-in</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SelfGarden;
